import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@versesverse/core';
import { nameFormsOf } from '@versesverse/schema/names';
import { compareValue, parseCompareValue } from '@versesverse/schema/compare-link';
import type { ApiFetch } from '@versesverse/core';
import { ROUTES, apiUrl } from '@versesverse/schema/api';
import { chunkedMap, defaultApiBase } from './config';
import { OfflineButton } from './pwa/OfflineButton';
import { configureWorker, requestOfflineDownload } from './pwa/register';
import { browserFetch } from './lib/fetch';
import { useLibrary } from './lib/useLibrary';
import type { LoadedLibrary } from './lib/useLibrary';
import { MapCanvas } from './map/MapCanvas';
const V8Map = lazy(() => import('./map/V8Map').then(m => ({ default: m.V8Map })));
import { enterZoom } from './map/v8/our-scene';
import type { FlyOrder } from './map/v8/orders';
import type { Orientation, Sector, Wash } from './map/controller';
import { MapOrientation } from './map/MapOrientation';
import { useTrail } from './trail/useTrail';
import { MapStandpoint } from './trail/MapStandpoint';
import { MapYou } from './trail/MapYou';
import { MapLegend } from './map/MapLegend';
import { MapFilterBar } from './map/MapFilterBar';
import { MapPresenceCluster } from './map/MapPresenceCluster';
import { mockExploring, mockRooms, mocksWanted } from './map/v8/mocks';
import type { FilterChip } from './map/MapFilterBar';
import { MapHint } from './map/MapHint';
import { MapHoverCard } from './map/MapHoverCard';
import { MapTravel } from './travel/MapTravel';
import { regionsFrom } from './travel/regions';
import type { Hint } from './map/v8/hint';
import { usePresence } from './map/usePresence';
import { NO_SCRIPTURE_COLOUR, SHAPE_BY_TYPE, SocialClient, buildEdges, buildNodes, compilationBase, compilationFiles, contentFiles, cookieAuthorize, edgesFromSkeleton, hasIdentityPlane, indexNodes, nodesFromSkeleton, tieKey, tieRefs, zonesFor } from '@versesverse/core';
import { reachOf } from './trail/trail';
import { SearchBox } from './search/SearchBox';
import { NodePanel, TYPE_KEY } from './panel/NodePanel';
import { trailsFrom } from './panel/trails';
import { coverage } from './map/v8/walk';
import { hoverCard } from './map/v8/hover';
import { otherName } from './search/other-name';
import type { TrailTarget } from './panel/trails';
import { ReadingDrawer } from './reader/ReadingDrawer';
import { SideBySide } from './story/SideBySide';
import { DisputedPanel } from './story/DisputedPanel';
import { SourcesPanel, heldBackFor, sourceTextFor, sourceTextForReports } from './story/SourcesPanel';
import { ABSENT_BRANCHES } from '@versesverse/schema/absent';
import { UNSHELVED } from '@versesverse/schema/unshelved';
import { browserUrlHost, readAppUrl, syncAppUrl } from './nav/permalink';
import { documentTitle } from './nav/title';
import { pagesFallback } from './nav/rescue';
import type { AppUrlState } from './nav/permalink';
import { useNodeVerses } from './verses/useNodeVerses';
import { useNodeReports } from './reports/useNodeReports';
import { useContextVerses } from './verses/useContextVerses';
import { useSidePassages } from './verses/useSidePassages';
import { useSelectionHistory } from './nav/selectionHistory';
import { DiscussionDrawer } from './social/DiscussionDrawer';
import { Rooms } from './social/Rooms';
import { HallPage } from './social/HallPage';
import { Account } from './social/Account';
import { ReportedPosts } from './social/ReportedPosts';
import { HallsPanel } from './social/HallsPanel';
import { livekitConnector } from './social/livekitConnector';
import { LangProvider, useLang } from './i18n/lang';
import type { Strings } from './i18n/strings';
import { LanguageSwitcher } from './i18n/LanguageSwitcher';
import { Tour } from './walk/Tour';
import { useArrival } from './walk/useArrival';

/*
 * The reader's shell. It opens the library on mount and shows one of three honest states —
 * loading, unreachable, or the map — rather than a blank canvas that might mean either "still
 * arriving" or "broken". `apiBase` and `fetch` are props with real defaults so a test drives the
 * whole thing with a fake network.
 */
export interface AppProps {
  apiBase?: string;
  fetch?: ApiFetch;
  /**
   * Draw the map from its own artifact rather than from the bundle (D26).
   *
   * A prop with the build-time flag as its default, for the same reason `apiBase` and `fetch` are
   * props: a test has to be able to drive BOTH paths, and the one thing worth asserting about this
   * change is that they produce the same map.
   */
  chunkedMapFlag?: boolean;
}

export function App(props: AppProps): React.JSX.Element {
  /* One language for the whole shell — loading, error, and map alike read from the same context. */
  return (
    <LangProvider>
      <AppShell {...props} />
    </LangProvider>
  );
}

function AppShell({ apiBase = defaultApiBase, fetch = browserFetch, chunkedMapFlag = chunkedMap }: AppProps): React.JSX.Element {
  const state = useLibrary(apiBase, fetch, chunkedMapFlag);
  const { t } = useLang();

  if (state.status === 'loading') {
    return <CenteredStatus role="status" live="polite">{t('shell.loading')}</CenteredStatus>;
  }
  if (state.status === 'error') {
    /*
     * A DEAD END IS THE ONE THING THIS SCREEN MUST NOT BE — see `nav/rescue.ts`.
     *
     * The reader who meets it has just followed one of the 52,463 links the static pages carry.
     * They did not wander in; they came for Abraham, from a page that was open in front of them a
     * second ago. And the half of this library that page belongs to needs NONE of what just failed:
     * no manifest, no bundle, no API, no JavaScript.
     *
     * So: try again, and read it as a page. Both are things a reader can actually do, and neither
     * is on screen unless something is already broken.
     */
    return (
      <CenteredStatus
        role="alert"
        error
        actions={
          <>
            <button type="button" className="status-again" onClick={() => { window.location.reload(); }}>
              {t('shell.retry')}
            </button>
            <a className="status-pages" href={pagesFallback(document.referrer, window.location.origin)}>
              {t('shell.pages')}
            </a>
          </>
        }
      >
        {t('shell.error', { detail: describeError(state.error) })}
      </CenteredStatus>
    );
  }
  return <MapView library={state.library} apiBase={apiBase} fetch={fetch} />;
}

/** The map, once the library is open: the canvas, and a small overlay that names what was tapped. */
/** Verses each side to open the reader with, and to add on each "wider"; and the widest it grows. */
const READ_STEP = 4;
const READ_MAX = 40;


function MapView({ library, apiBase, fetch }: { library: LoadedLibrary; apiBase: string; fetch: ApiFetch }): React.JSX.Element {
  const { bundle, manifest, content } = library;
  const { t } = useLang();
  /* The selected node and the trail back to how you got there — so wandering figure to figure has a
     way home. `navigate` opens a node (or closes with null); `back` steps to the previous. */
  const { selected, canGoBack, navigate, back } = useSelectionHistory();
  /** The verse opened in the expanded reader (a verse id), and how many neighbours it shows each side —
   *  the two sides grow independently, so scrolling up loads prior verses without touching what is below. */
  const [reading, setReading] = useState<number | null>(null);
  const [readBefore, setReadBefore] = useState(READ_STEP);
  const [readAfter, setReadAfter] = useState(READ_STEP);
  /** A pairing opened for side-by-side reading — a shared story or a "told twice" retelling. */
  const [openSide, setOpenSide] = useState<{
    title: string; leftLabel: string; rightLabel: string; leftIds: number[]; rightIds: number[]; caption?: string;
    /** How this pairing addresses itself in the URL — set by each open*, read by the URL sync. */
    link: string;
  } | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  /** The "where people disagree" panel — disputed figures, and the passages measured as contested. */
  const [disputedOpen, setDisputedOpen] = useState(false);
  /** The contested passage the reader has open, by its ref — what the address bar carries, so the
   *  URL is the shareable thing and no "copy link" control has to exist. Null when none is open. */
  const [openPassageRef, setOpenPassageRef] = useState<string | null>(null);
  /** Whether the figure's discussion drawer is open. */
  /* The open discussion — a whole figure, or a single verse. `key` is the opaque scope the server
     threads posts by; a verse keys by its stable citation, never its build-specific id. */
  const [discussScope, setDiscussScope] = useState<{ key: string; title: string } | null>(null);
  /** The onboarding hint shows until the reader has opened ANYTHING — a figure, a browse, the reader,
   *  a discussion — then never returns; it is the first nudge, not a permanent chrome, so once the
   *  reader has clearly engaged it should not linger at the foot of a panel they are reading. */
  const [hintDismissed, setHintDismissed] = useState(false);
  /* What the map says to do from here. Only the ported renderer reports it; the old one leaves it
     undefined and the hint keeps its original one-shot wording. */
  const [hint, setHint] = useState<Hint | undefined>(undefined);
  /* The name under the pointer, and where it is — reported by BOTH renderers since 2026-08-22.
     It said "only the ported renderer" until then, and it was true: `MapHoverCard` is not gated on
     the flag, so the card rendered for nobody on the map every reader gets. */
  const [hovered, setHovered] = useState<{ name: string; sub: string } | null>(null);
  /* The travel list, opened from the orientation pill at the foot of the map. */
  const [travelOpen, setTravelOpen] = useState(false);
  /*
   * THE CAMERA GOES WITH A READER WHO LEAVES THE ARRIVAL.
   *
   * Skipping set the phase, lifted the fog and cleared the landing, and left the camera exactly where
   * the flight had put it: ten times zoomed in on one name, with the core's bloom filling the screen.
   * A reader who pressed "skip to the whole map" got a close-up of Lord (Adonai).
   *
   * It survived because every comparison in e2e/ scrolls out before it measures anything, so the
   * state that button actually produces had never been looked at.
   *
   * Here rather than in `useArrival` because the arrival must STOP driving the camera once it is
   * skipped — its own test says so, and rightly: a stop that stays set is a standing order. This is
   * one object, issued once, consumed by identity like every other fly order.
   */
  const [wholeField, setWholeField] = useState<FlyOrder | null>(null);
  const [hoverAt, setHoverAt] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  /*
   * HOW MUCH OF THE SCREEN THE PANEL HAS TAKEN, for the floating chrome to keep clear of.
   *
   * Their `renderVals` carries this as `reserveW` — `min(430px, 44vw)` while a name is open and
   * zero otherwise — and every floating control is laid out against it. Ours had no such notion, so
   * the feed drew straight over the panel's heading and the filter row ran underneath it. A fixed
   * element cannot know about a sibling, so the width goes on the document and the CSS reads it.
   */
  useEffect(() => {
    const root = document.documentElement;
    if (selected === null) root.style.removeProperty('--panel-reserve');
    else root.style.setProperty('--panel-reserve', 'var(--panel-w)');
    return () => { root.style.removeProperty('--panel-reserve'); };
  }, [selected]);

  const engaged = Boolean(selected) || sourcesOpen || disputedOpen || openSide !== null || reading !== null || discussScope !== null;
  useEffect(() => { if (engaged) setHintDismissed(true); }, [engaged]);
  /** Whether the menu behind the wordmark is open. Everything but the search lives in it, at every
   *  width, so the map's corner is one pill — see the note where it is rendered. */
  const [moreOpen, setMoreOpen] = useState(false);
  /*
   * AND ESCAPE CLOSES IT, which the note beside the scrim below has claimed since the menu was
   * written while nothing implemented it. Measured: press the wordmark and the menu opens; press
   * Escape and it is still open. The scrim then intercepts the wordmark itself, so the one control
   * that does close it cannot be pressed until the scrim is clicked first.
   *
   * `MapYou` does the same thing for the reader's card a few lines down. Two overlays, one rule.
   */
  useEffect(() => {
    if (!moreOpen) return undefined;
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); };
  }, [moreOpen]);
  /* The reader's own card, in the corner — see `MapYou`. Held here like every other overlay. */
  const [youOpen, setYouOpen] = useState(false);
  /** The figure in front of the reader — pushed by the engine when it changes, never polled. It is
   *  what the orientation pill names. */
  const [orientation, setOrientation] = useState<Orientation | null>(null);

  /*
   * Tell the offline worker where content and the manifest live, once, as soon as the manifest is in
   * hand — it cannot guess another origin's URLs, and the app reads the manifest before it fetches a
   * single content file, so this always lands before the worker has to decide anything. Production
   * only, matching where the worker is registered (main.tsx).
   */
  useEffect(() => {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    if (!import.meta.env.PROD || !nav?.serviceWorker) return;
    const manifestUrl = apiUrl(apiBase, ROUTES.manifest.path());
    void nav.serviceWorker.ready.then((registration) => {
      configureWorker(
        nav.serviceWorker.controller ?? registration.active,
        manifest.content.baseUrl,
        manifestUrl,
        /* Where the compilation is answered from, and which build (D26) — so the worker can hold it in
           a partition per build rather than passing it through and leaving an offline reader mapless. */
        { base: compilationBase(apiBase), version: manifest.version },
      );
    /* A worker that never becomes ready is an offline feature that did not arrive; there is no
       sentence a reader could act on, and a rejection nobody holds is noise in its place. */
    }).catch(() => {});
  }, [apiBase, manifest]);

  /*
   * The whole build, as the exact URLs the client fetches — what "save for offline" pulls down.
   *
   * THE COMPILATION IS IN THE LIST TOO (D26), and it has to be from the moment the worker caches it
   * rather than from the moment the bundle stops carrying the layer: an offline download that pulled
   * the scripture and not the map would leave a reader on a plane with every verse and no way to reach
   * one. The skeleton is what the map needs; the records are per figure and are not pulled in bulk,
   * because "download everything" is the one place a whole-layer fetch is the reader's own request
   * (§4.9) and that is a deliberate scoping decision rather than an oversight — see §10.
   */
  const offlineFiles = useMemo(
    () => [...contentFiles(content, bundle), ...compilationFiles(apiBase, manifest.version)],
    [content, bundle, apiBase, manifest.version],
  );
  const offlineSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

  /*
   * Built once per bundle, not per render — the render model is the map's whole node/edge set.
   *
   * TWO DOORS, ONE PICTURE (`ARCHITECTURE.md D26`). With the chunked flag on and the skeleton fetched,
   * the map is built from that; otherwise from the bundle, exactly as it always was. `core/map-model.ts`
   * asserts the two produce identical render models, so this branch cannot change what a reader sees —
   * only how many bytes it took to show them.
   */
  const { nodes, edges, washes, sectors } = useMemo(() => {
    /*
     * The washes come through whichever door the rest of the map did, and are resolved to colours
     * here — the renderer holds no corpus registry, the same way it holds no palette for the arcs.
     * A text in `fields` that this palette has never heard of is dropped rather than drawn grey:
     * a wash is a claim about WHICH scripture is thick here, and an unnameable one claims nothing.
     */
    const toWashes = (fields: Record<string, { x: number; y: number; n: number; r: number }[]>): Wash[] =>
      bundle.corpora.flatMap(c => (fields[c.id] ?? []).map(f => ({ ...f, colour: c.colour })));

    /* A wedge naming a text this palette does not carry is dropped: the label's whole job is to
       say WHICH scripture lies this way, and it cannot be written without the name. */
    const toSectors = (wheel: { id: string; a: number; width: number; r: number; only: number }[]): Sector[] =>
      wheel.flatMap(w => {
        const corpus = bundle.corpora.find(c => c.id === w.id);
        return corpus ? [{ ...w, label: corpus.name.toUpperCase(), colour: corpus.colour }] : [];
      });

    if (library.skeleton) {
      return {
        nodes: nodesFromSkeleton(library.skeleton, bundle.corpora),
        edges: edgesFromSkeleton(library.skeleton),
        washes: toWashes(library.skeleton.fields ?? {}),
        sectors: toSectors(library.skeleton.wheel ?? []),
      };
    }
    const built = buildNodes(bundle.nodes, bundle.layout, bundle.corpora);
    return {
      nodes: built,
      edges: buildEdges(bundle.ties, indexNodes(built)),
      washes: toWashes(bundle.fields ?? {}),
      sectors: toSectors(bundle.wheel ?? []),
    };
  }, [bundle, library.skeleton]);

  /*
   * THE VERSE BEHIND EACH STRAND, for the open name's fan to write along it.
   *
   * FROM THE BUNDLE, WHICH BOTH DOORS FETCH — so both doors get the writing, and this note used to
   * say otherwise.
   *
   * It read: "a reader who came through the streamed door sees strands with no writing on them",
   * reasoning from the skeleton's edges being three numbers each with no room for a citation string.
   * That part is true and it is not the question. The refs do not come from the door at all: they
   * come from `bundle.ties`, and `useLibrary` fetches the bundle AND the skeleton together — the
   * skeleton replaces how the map's NODES AND EDGES are built, not what the app knows.
   *
   * Measured 2026-08-22 rather than reasoned, because reasoning is what got it wrong the first time.
   * Built with `VITE_CHUNKED_MAP=1`, confirmed by the network that `…/map/skeleton` was fetched 200
   * — a missing skeleton looks exactly like the flag being off, which is the trap `skeletonError`
   * warns about — and with Abraham open the fan wrote SIX passages at every zoom, the same six the
   * bundle door writes.
   *
   * The per-figure citations on the node record are still the right shape for the streamed path and
   * still unbuilt (`V8-PORT.md` §5). What is no longer true is that anything is missing without them.
   */
  const refs = useMemo(() => tieRefs(bundle.ties), [bundle.ties]);

  /*
   * WHAT THE READER HAS PUT AWAY — the filter row along the bottom, and the state behind it.
   *
   * `map/v8/web.ts` has read `c_<corpus>` and `k_<kind>` since it was ported and until now nothing
   * could set one: `V8Map` passed a literal `{}`. The reference puts these along the bottom edge as
   * always-visible toggles, where hiding a scripture is one tap; ours were folded inside the legend
   * accordion, where the same act cost three. Only the ported renderer honours them, so the row is
   * only shown with it.
   */
  /*
   * WHO ELSE IS HERE — invented today, and only when asked for.
   *
   * `?mock=1` is the only thing that fills these; without it the cluster renders nothing at all
   * rather than an empty panel. Each piece names its real source in `map/v8/mocks.ts`: the count is
   * a sum over `usePresence`, the rooms are `topRooms`, the feed is the comms layer.
   */
  const mocked = useMemo(() => mocksWanted(window.location.search), []);
  const feedNames = useMemo(() => nodes.slice(0, 400).map(n => n.label), [nodes]);
  const social = useMemo(() => ({
    exploring: mocked ? mockExploring() : null,


    rooms: mocked ? mockRooms(feedNames, 2) : [],
  }), [mocked, feedNames]);

  const [off, setOff] = useState<Record<string, boolean | undefined>>({});
  const toggleFilter = useCallback((key: string) => {
    setOff(was => ({ ...was, [key]: was[key] === true ? undefined : true }));
  }, []);
  /*
   * NARROW THE MAP TO ONE SCRIPTURE, from the panel row that names it (engine.js:2198).
   *
   * Theirs sets it and leaves it; ours makes a second press give everything back. The reader's
   * question is "which of these is which", not "hide the rest permanently", and once they have their
   * answer the way out should be the control they came in by rather than a trip to the filter bar.
   */
  const onlyCorpus = useCallback((id: string) => {
    setOff((was) => {
      const alone = bundle.corpora.every(c => (c.id === id) === (was[`c_${c.id}`] !== true));
      const next = { ...was };
      for (const c of bundle.corpora) next[`c_${c.id}`] = alone ? undefined : c.id !== id;
      return next;
    });
  }, [bundle.corpora]);
  const filterChips = useMemo((): FilterChip[] => {
    const scriptures = bundle.corpora.map(c => ({
      key: `c_${c.id}`,
      label: c.name ?? c.id,
      colour: c.colour,
      shape: 'disc' as const,
    }));
    /* One control per SHAPE actually on the map, not per kind: fourteen kinds share four marks, and
       a row naming all fourteen would be the wall of controls this replaced. */
    const shapes: FilterChip[] = [];
    const seen = new Set<string>();
    for (const node of nodes) {
      if (seen.has(node.type)) continue;
      seen.add(node.type);
      const shape = SHAPE_BY_TYPE[node.type] ?? 'circle';
      shapes.push({
        key: `k_${node.type}`,
        label: t(TYPE_KEY[node.type] ?? 'type.unknown'),
        colour: NO_SCRIPTURE_COLOUR,
        shape: shape === 'square' ? 'square' : shape === 'diamond' ? 'diamond' : 'disc',
      });
    }
    return [...scriptures, ...shapes.slice(0, 6)];
  }, [bundle.corpora, nodes, t]);

  /*
   * WHAT THE PORTED RENDERER NEEDS FROM THE REGISTRY, and nothing else.
   *
   * A colour per text and the traditions that hold it. The second is a LIST, not a name: the Hebrew
   * Bible is held by two, and counting books rather than traditions is exactly the mistake
   * `RenderNode.shared` exists to warn about — four Jewish texts would otherwise make a purely
   * Jewish figure look like one the traditions share.
   */
  const corpusColour = useMemo(
    () => Object.fromEntries(bundle.corpora.map(c => [c.id, c.colour])),
    [bundle],
  );
  const traditionsOf = useMemo(() => {
    const held = new Map(bundle.corpora.map(c => [c.id, c.traditions ?? []]));
    return (corpus: string): readonly string[] => held.get(corpus) ?? [];
  }, [bundle]);

  /* Read once, from the address bar: a flag to look at, not a setting to keep. */
  const v8Flag = useMemo(() => new URLSearchParams(window.location.search).get('v8') === '1', []);


  /*
   * The words the ported map writes, which are ours and not the engine's.
   *
   * A scripture's own name is NOT translated — the Qur'an is the Qur'an in all three languages —
   * so the label comes from the registry, while the count phrase around it comes from the
   * dictionary, where Hebrew and Arabic put the number somewhere English does not.
   */
  const v8Words = useMemo(() => {
    /* Uppercased on the way OUT, not on the way in: a bundle may carry a corpus with no name at
       all, and this hook runs on every render whether the flag is on or not. */
    const named = new Map(bundle.corpora.map(c => [c.id, c.name] as const));
    return {
      wedgeLabel: (corpus: string): string => (named.get(corpus) ?? '').toUpperCase(),
      /*
       * BOTH NUMBERS WHEN THERE ARE BOTH, because one alone is misread. The line said
       * "11 named here only" under the Quran and Ariel read it as the Quran having eleven names.
       * It has 115, across 4,304 passages; eleven is how many no other text names — which on a map
       * of shared scripture is the more interesting number and the easier one to take for a total.
       *
       * Theirs writes the exclusive count alone and gets away with it because its petals hold 935
       * and 2,438 of them. Ours holds eleven.
       */
      countLine: (of: { held?: number; alone: number }): string => (of.held === undefined
        ? t('map.wedgeOnly', { count: of.alone })
        : t('map.wedgeCounts', { held: of.held, alone: of.alone })),
      /* The wheel shouts; a sentence does not. See OurWords. */
      corpusName: (corpus: string): string => named.get(corpus) ?? corpus,
      /* The card under the pointer. `TYPE_KEY` is the panel's own kind table, so a figure is called
         the same thing hovered as it is once opened. */
      kindName: (kind: string): string => t(TYPE_KEY[kind] ?? 'type.unknown'),
      corporaCount: (n: number): string => t('map.hoverCorpora', { count: n }),
      joinNames: (names: readonly string[]): string => names.join(t('map.hoverJoin')),
    };
  }, [bundle, t]);

  /*
   * THE READOUT'S WORDS. Theirs writes them as English literals into its markup; a reader who has
   * put the interface into Hebrew should not meet "strands off at this range" in the corner, so
   * they come from the dictionary like everything else and fall back to English where a language
   * has not filled them in yet.
   */
  const v8HudWords = useMemo(() => ({
    fps: t('map.hud.fps'),
    ms: t('map.hud.ms'),
    cells: t('map.hud.cells'),
    sky: t('map.hud.sky'),
    names: t('map.hud.names'),
    disc: t('map.hud.disc'),
    dot: t('map.hud.dot'),
    clump: t('map.hud.clump'),
    strands: t('map.hud.strands'),
    strandsOff: t('map.hud.strandsOff'),
    chunks: t('map.hud.chunks'),
    kilobytes: t('map.hud.kilobytes'),
    tilt: (percent: number): string => t('map.hud.tilt', { percent }),
    zoom: (zoom: string, floor: string): string => t('map.hud.zoom', { zoom, floor }),
    labels: (count: number): string => t('map.hud.labels', { count }),
    fan: (strands: number, passages: number): string => t('map.hud.fan', { strands, passages }),
    split: (still: string, rate: number, live: string): string => t('map.hud.split', { still, rate, live }),
    layers: (of: { travellers: number; seats: number; readers: number; dust: number; disputed: number }): string =>
      t('map.hud.layers', of),
  }), [t]);

  /*
   * WHAT A SEARCH RESULT IS — the same sentence the map says under the pointer.
   *
   * A row reading only "Abraham" makes a reader choose between things they cannot tell apart, and
   * this build carries 21 name collisions: the King James renders Joshua as "Jesus" twice, Saul is
   * the king and the man who became Paul. Theirs answers it in the row, and so does ours now.
   *
   * Built from `hoverCard` rather than beside it. The card under the pointer and the row in the list
   * answer the same question about the same figure, and a second spelling of that answer would be a
   * second thing to keep in step — which is how the two ever come to disagree.
   */
  const describeResult = useCallback((node: { corpora: string[]; type?: string; name?: string; arabic?: string | null }) => {
    const colours = node.corpora.map(c => corpusColour[c]).filter((c): c is string => c !== undefined);
    /*
     * THE NAME THE OTHER TRADITION USES, when it is not already in the one we are showing.
     *
     * 67 of the 1,099 carry a second name and the canonical name embeds it for some of them —
     * "Enoch / Idris" already says both, while "Noah" and "Abraham" do not. Writing it again would
     * give "Enoch / Idris / Idris", so it is shown only when it is genuinely absent.
     */

    return {
      also: otherName(node.name ?? '', node.arabic),
      sub: hoverCard(
        {
          name: '',
          corpora: node.corpora.map(c => v8Words.corpusName(c)),
          kind: t(TYPE_KEY[node.type ?? ''] ?? 'type.unknown'),
        },
        { count: v8Words.corporaCount, join: v8Words.joinNames },
      ).sub,
      /* The scripture that names it first in registry order, so the dot matches its circle. */
      colour: colours[0] ?? NO_SCRIPTURE_COLOUR,
    };
  }, [corpusColour, v8Words, t]);

  /*
   * WHAT THIS READER HAS WALKED. Recorded from here on, so the record is accumulating before
   * anything reads it.
   *
   * TODO(universe-map): the FOG this feeds — a figure drawing at full strength when walked, .42
   * within reach and .1 beyond it — is not applied to the canvas yet, and must not be until the
   * `ground` walk exists (step 4). Switched on now it would dim the whole map to a tenth for every
   * reader on their first visit, because nobody has walked anywhere. The travel overlay, the
   * journey record and the standpoint menu read from here too.
   */
  const trail = useTrail();
  useEffect(() => {
    if (selected === null) return;
    const node = nodes.find(n => n.id === selected);
    if (node) trail.walked({ id: node.id, corpora: node.corpora, contested: node.contested });
  }, [selected, nodes, trail]);

  /* The nearest and furthest the reader has been, looked up in the map they are looking at NOW —
     the journey itself stores ids and no rung, so a re-tiered figure cannot contradict the map. */
  /*
   * HOW MUCH OF EACH SCRIPTURE THE READER HAS OPENED — their YOUR ATLAS.
   *
   * `coverage` is the ported, differentially-checked count in `map/v8/walk.ts`; it was typed to the
   * engine's numeric ids, which is why nothing outside the renderer could call it and why this block
   * of their reader's menu had no answer here for weeks. It is generic in the id now, so this is the
   * same implementation the map uses rather than a second one that could disagree with it.
   */
  const atlas = useMemo(() => {
    const walked = new Set(trail.trail.visited);
    const rows = coverage(
      nodes.map(n => ({ id: n.id, cs: n.corpora })),
      walked,
      bundle.corpora.map(c => c.id),
    );
    const named = new Map(bundle.corpora.map(c => [c.id, c] as const));
    return rows.map(row => ({
      ...row,
      name: named.get(row.key)?.name ?? row.key,
      colour: named.get(row.key)?.colour ?? NO_SCRIPTURE_COLOUR,
    }));
  }, [nodes, trail.trail.visited, bundle.corpora]);

  const trailRecord = useMemo(
    () => trail.record(id => nodes.find(n => n.id === id)?.tier ?? null),
    [trail, nodes],
  );

  /*
   * WHO STANDS BESIDE WHOM — built from the render edges rather than from `bundle.ties`, so it comes
   * through whichever of D26's two doors the map itself did and cannot disagree with what is drawn.
   *
   * Three things read it: the frontier (what is within one step of what the reader has walked), the
   * places offered at the end of the arrival, and the names the ground walk offers when a passage
   * quotes nobody.
   */
  const neighbours = useMemo(() => {
    const map = new Map<string, string[]>();
    const add = (from: string, to: string): void => {
      const list = map.get(from);
      if (list) list.push(to); else map.set(from, [to]);
    };
    for (const edge of edges) {
      const a = nodes[edge.a]?.id;
      const b = nodes[edge.b]?.id;
      if (a === undefined || b === undefined) continue;
      add(a, b);
      add(b, a);
    }
    return map;
  }, [nodes, edges]);

  /*
   * WHERE A READER CAN GO NEXT from whichever figure is open — the chips at the foot of the panel.
   *
   * Built here, like `sharesWith` and `identities`, because it needs three things the panel does not
   * hold: the tie graph, every node's place on the map, and the citation each tie was drawn from.
   *
   * The RING is the distance from the centre, not a stored tier. A radial layout already says which
   * ring a figure is on by where it sits, and "inward" means visibly nearer the middle — deriving it
   * keeps the chip honest about the map the reader is looking at rather than about a number in the
   * build that the layout could drift from.
   */
  const trailTargets = useMemo(() => {
    const map = new Map<string, TrailTarget>();
    for (const node of nodes) {
      map.set(node.id, {
        id: node.id,
        name: node.label,
        colour: node.color,
        ring: Math.hypot(node.x, node.y),
        glyph: node.shape === 'circle' ? 'round' : 'square',
      });
    }
    return map;
  }, [nodes]);

  const trails = useMemo(() => {
    if (selected === null) return [];
    const from = trailTargets.get(selected);
    if (from === undefined) return [];
    return trailsFrom(
      from,
      neighbours.get(selected) ?? [],
      id => trailTargets.get(id),
      (a, b) => refs.get(tieKey(a, b)) ?? null,
    );
  }, [selected, trailTargets, neighbours, refs]);

  /*
   * THE PLACES the opening flight visits, and the one it leaves the reader standing on.
   *
   * Derived from the tie graph rather than named — see `core/zones.ts`. Naming a figure here would
   * be a build-time claim the next rebuild could quietly falsify, leaving the arrival opening on
   * somebody the map no longer carries: a blank first screen, and the one screen a first-time
   * reader cannot recover from.
   */
  const places = useMemo(
    () => zonesFor(nodes.map(n => ({
      id: n.id, name: n.label, type: n.type, x: n.x, y: n.y, corpora: n.corpora,
      ties: neighbours.get(n.id) ?? [],
    }))),
    [nodes, neighbours],
  );

  /*
   * THE NEIGHBOURHOODS TO RETURN TO — the rows behind the orientation pill.
   *
   * Built from `places`, which is what the opening flight already visits, so the map has one idea of
   * where its neighbourhoods are rather than two. Theirs reads a stored `region` off each node; ours
   * has no such field, and deriving them keeps the list from claiming a partition nobody curated —
   * see `travel/regions.ts`.
   */
  const travelRegions = useMemo(
    () => regionsFrom(
      places.map(p => ({ id: p.id, name: p.name })),
      id => neighbours.get(id) ?? [],
      new Set(trail.trail.visited),
    ),
    [places, neighbours, trail.trail.visited],
  );

  /*
   * A point out past the outermost names — where the naming stops, which the flight passes on its
   * way in. Taken from the field rather than assumed: how far out this map reaches is a fact about
   * the roster, and a constant would fly past empty sky the day it grew.
   */
  const rim = useMemo(() => {
    let far = { x: 0, y: 0, d: 0 };
    for (const n of nodes) {
      const d = Math.hypot(n.x, n.y);
      if (d > far.d) far = { x: n.x, y: n.y, d };
    }
    return { x: far.x, y: far.y };
  }, [nodes]);

  /*
   * THE ARRIVAL — the field at full extent, a flight across it, and one name at the end.
   *
   * It replaces the tradition doors this app used to open with. The doors asked who a reader was
   * before they had read anything; this shows them what is here and leaves them somewhere in it.
   */
  /*
   * THE SAME FACT AS `rim`, AS A DISTANCE — how far out the naming stops.
   *
   * Derived from the point rather than measured again: `our-scene` takes its rim as the largest
   * `hypot` over the same nodes, so these are one quantity, and an absolute zoom rescaled by this
   * lands where the engine puts it. Two traversals would be two answers the day one of them changed.
   */
  const rimRadius = useMemo(() => Math.hypot(rim.x, rim.y), [rim]);
  /*
   * READ ONCE, AT MOUNT, BEFORE ANYTHING CLEARS IT.
   *
   * Whether this reader ASKED for something — a figure, a passage, a comparison, a panel — decides
   * whether the arrival runs at all, and the answer has to be taken from the address bar before the
   * restore effect starts writing to it. `useState` with an initialiser reads it exactly once.
   */
  const [askedOnArrival] = useState(() => {
    const asked = readAppUrl(window.location.search);
    return Boolean(asked.node ?? asked.passage ?? asked.compare ?? asked.panel ?? asked.discuss ?? asked.hall);
  });
  const arrival = useArrival(places, rim, trail.trail.visited.length, askedOnArrival);
  /*
   * WHERE A READER WHO LEAVES THE ARRIVAL IS PUT DOWN.
   *
   * It used to be `{ zoom: 1 }` — the floor, the widest view the map has. That was a fix for the
   * skip leaving the camera ten times zoomed in on one name, and it overshot: the reader landed on
   * the emptiest screen in the app, with the map's own hint reading "the dark is what you have not
   * surveyed". Theirs never opens there; `THEIR_ZOOMS.enter` carries their number and the reason.
   */
  const enterMap = useCallback(
    (at: number, on: { x: number; y: number } | null): FlyOrder =>
      ({ to: on, zoom: { absolute: enterZoom(at) }, durMs: 0 }),
    [],
  );

  /*
   * AND IT OPENS ON A NAME, not on the middle of the field.
   *
   * Theirs is `cam = { x: first.x, y: first.y, z: z0 }` — the first node of its roster, which is
   * also where its own walk begins. Ours pointed at the origin, and the origin is a place rather
   * than a figure: it happens to hold the divine names here, which looked fine for a reader who has
   * walked nowhere and was wrong for everybody else.
   *
   * With a walk in storage the fog closes over everything unvisited, so a returning reader entering
   * at the origin arrived somewhere the fog had shut — ONE name written where a first-time reader
   * gets twelve. They now enter where their reading started, which is what theirs does and what the
   * fog assumes.
   */
  const firstName = useMemo(() => {
    const one = nodes[0];
    return one === undefined ? null : { x: one.x, y: one.y };
  }, [nodes]);

  /*
   * AND IT IS ISSUED ON EVERY ROUTE INTO THE MAP, not only when a reader skips.
   *
   * This hung off the two Skip handlers, so a RETURNING reader never got it: `useArrival` starts
   * them on the map — "somebody who has been here is never flown in again" — and with no arrival to
   * skip, nothing ever asked the camera to go anywhere. They landed on the floor, which is the exact
   * screen the entry zoom exists to stop anybody landing on.
   *
   * It was invisible because every harness in `e2e/` reached the map by pressing Skip. Giving ours
   * the same walk as theirs is what surfaced it — with a trail in storage there is no Skip to press,
   * and `hint-v8` went red on the first run.
   *
   * Theirs does the same thing and does not have the distinction: engine.js:354 places every reader
   * at `max(minZoom(), 0.6)` once there is a node to place them on.
   */
  const entered = useRef(false);
  useEffect(() => {
    /* The rim is nought until the roster is in; entering before that would divide by it. */
    if (entered.current || !arrival.open || !(rimRadius > 0)) return;
    entered.current = true;
    setWholeField(enterMap(rimRadius, firstName));
  }, [arrival.open, enterMap, rimRadius, firstName]);

  /* Where the flight left the reader. The map's own selection follows it, so the panel opens on the
     name they are standing on the moment the arrival is over. */
  const landedId = arrival.landed?.id ?? null;
  useEffect(() => {
    if (landedId === null) return;
    navigate(landedId);
  }, [landedId, navigate]);

  /*
   * THE FOG, at last drawn rather than only recorded.
   *
   * The frontier is derived from the ties on demand rather than stored: storing it would be a
   * second copy of the graph, going stale the moment the graph is rebuilt.
   */
  const fogWalked = trail.trail.visited;
  const fogWithin = useMemo(
    () => [...reachOf(fogWalked, id => neighbours.get(id) ?? [])],
    [fogWalked, neighbours],
  );
  /*
   * THE FOG IS OFF, on Ariel's instruction, 2026-08-22: *"just drop the code that prevents clicking
   * on nodes"*, and *"lets disable the feature for now, make all visible"*.
   *
   * IT NEVER BLOCKED A CLICK, AND THAT IS WHY IT TOOK SO LONG TO FIND. `hitTest` does not consult
   * the fog, and `hit` reaches 26px regardless of how small a circle is drawn, so a fogged name was
   * always openable — by anyone who could see it. It drew unwalked names at `FOG_BEYOND`, which is
   * **0.1**: a tenth of full strength, over a near-black field. A reader with a short trail was
   * being shown a few dozen lit names and 2,400 they had no way to know were there. "I cannot click
   * on nodes" is exactly what that looks like from the reader's side, and every search I ran for a
   * gate in the click path came back empty because there is no gate in the click path.
   *
   * `mix` is the whole switch — `fogFor` returns `1 - mix * (1 - deep)`, so a mix of 0 is every
   * name at full strength with the walk still recorded underneath. Nothing else is torn out: the
   * trail still accumulates, `fogWithin` is still derived, and turning this back on is one word.
   */
  const FOG_ON = false;
  const fog = useMemo(
    () => ({ walked: fogWalked, within: fogWithin, mix: FOG_ON ? arrival.fogMix : 0 }),
    [fogWalked, fogWithin, arrival.fogMix, FOG_ON],
  );

  /* Stable per library, so the verse loader re-runs only when the selected figure changes. */
  const verseTables = useMemo(
    () => ({
      refs: bundle.refs,
      corpusOf: bundle.corpusOf,
      corpora: bundle.corpora,
      chunkStarts: bundle.chunkStarts,
      /* So each passage can name whose translation it is, and on what terms. */
      editions: bundle.editions,
      bookEditions: bundle.bookEditions,
      /* What the manuscripts say about the verses this edition prints and the earliest lack. */
      sections: bundle.sections,
      verseNotes: bundle.verseNotes,
      textualNotes: bundle.textualNotes,
      /* Every name and its spellings, indexed to match a verse's `mentions` (node positions), so the
         passage builder can turn a mentioned name in the text into a link to that name's own verses. */
      /* The derivation moved to `schema/names.ts` so the static pages could build the same table
         and link the same names. Written out here, it was a rule only this surface could see. */
      names: nameFormsOf(bundle.nodes),
    }),
    [bundle],
  );

  /* Citation → verse id, for restoring the reader from a URL. The reverse of bundle.refs (id → ref),
     built once; a permalink carries the stable citation because verse ids are re-numbered each build. */
  const refToId = useMemo(() => {
    const map = new Map<string, number>();
    bundle.refs.forEach((ref, id) => { if (!map.has(ref)) map.set(ref, id); });
    return map;
  }, [bundle.refs]);

  /* The social plane lives on the API server (VITE_API_BASE), not the CDN; the browser sends the
     session cookie for it automatically. Built once. */
  /*
   * THE SAME `fetch` THE REST OF THE APP USES — 2026-08-18.
   *
   * This was the global `fetch`, which meant nothing the social plane did was observable from a
   * test that injects a network. That is not a theoretical cost: a test written to prove the app
   * had stopped asking `auth/me` of a plane-less deployment passed identically WITHOUT the fix,
   * because the call it was watching for could never have reached the injected network at all.
   *
   * It was recorded as blocked on `ApiFetch` and `SocialFetch` not lining up. They line up exactly:
   * `ApiResponse` carries everything `SocialResponse` needs and one field more, and
   * `SocialRequestInit` satisfies every member of `ApiRequestInit`. A one-line probe under
   * `--strict` compiles with no cast. The obstacle was an assumption, not a type.
   */
  const socialClient = useMemo(
    () => new SocialClient({ apiBase, fetch, authorize: cookieAuthorize }),
    [apiBase, fetch],
  );

  /*
   * WHO IS STANDING WHERE, for the map to draw as seats and voice rings.
   *
   * Only asked for when there is a plane to ask and a map that draws it. Counts, never names — see
   * `usePresence`, and `V8Map`'s own `presence` prop for why the reader chips stay dark.
   */
  const presence = usePresence(socialClient, v8Flag && hasIdentityPlane(manifest));

  /* Who we are, if anyone — the room controls need it to know whose name to put controls beside.
     Signed out is the normal case and not an error: reading never needs an account. */
  const [meId, setMeId] = useState<string | null>(null);
  /* Their name as well as their id: an account control that cannot say WHOSE account it is does
     not help the person on a shared machine who most needs it. */
  const [meName, setMeName] = useState<string | null>(null);
  /* The reader ROLE, which this dropped on the floor — so a site moderator had every power the
     server would honour and no surface anywhere that offered one. */
  const [meRole, setMeRole] = useState<string | null>(null);
  /* A leader's page, by its address — the link they put in a YouTube description. */
  const [hallSlug, setHallSlug] = useState<string | null>(null);
  /*
   * The open hall's NAME, kept with the slug it belongs to so a move between halls cannot leave
   * the previous name in the tab. `HallPage` learns it three fetches in; `App` only ever had the
   * slug, which is why `viewSubject` returned null here and the one view built to be linked from
   * somebody else's YouTube description was the one view with no name of its own.
   *
   * The setter bails out on an unchanged pair. `onNamed` fires on every load and on a rename, and
   * a fresh object each time would be new state each time — a render loop, not a title.
   */
  const [hallNamed, setHallNamed] = useState<{ slug: string; name: string } | null>(null);
  const onHallNamed = useCallback((name: string) => {
    if (hallSlug === null) return;
    setHallNamed(prev => (prev?.slug === hallSlug && prev.name === name ? prev : { slug: hallSlug, name }));
  }, [hallSlug]);
  /* The halls directory — where a Hall is found and where one is started. */
  const [hallsOpen, setHallsOpen] = useState(false);
  useEffect(() => {
    /*
     * DON'T ASK A DEPLOYMENT THAT HAS NO ONE TO ASK ABOUT.
     *
     * A content-only deploy loads no identity plane, so `auth/me` does not exist. This asked anyway,
     * every page load, and the browser refused the response before the app saw it — the request needs
     * credentials, the CORS policy of a plane-less server sends no `Access-Control-Allow-Credentials`,
     * and the resulting console error is written by the browser where no `catch` can reach it. The
     * `.catch` below was never the problem; it was doing its job over a request that should not have
     * been made.
     *
     * `!== false` rather than `=== true`: a server older than this client says nothing here, and the
     * safe reading of silence is the old behaviour — ask, and treat a refusal as signed out — rather
     * than deciding a working plane is absent and hiding every account control.
     */
    if (!hasIdentityPlane(manifest)) {
      setMeId(null);
      setMeName(null);
      setMeRole(null);
      return;
    }
    let live = true;
    socialClient.me()
      .then(me => {
        if (!live) return;
        setMeId(me?.id ?? null);
        setMeName(me?.displayName ?? null);
        setMeRole(me?.role ?? null);
      })
      /*
       * A FAILURE TO ASK IS NOT AN ANSWER — and this dropped it, so every panel below concluded the
       * reader was a stranger.
       *
       * `me()` used to answer null for any non-ok response, so a 500 or a proxy's 502 read exactly
       * like a 401: no account controls, no "rooms you have open", no moderation queue, no Hall
       * chooser, and the discussion offering a sign-in to somebody already signed in. It now throws
       * for anything that is not a refused session, and what is left here is to KEEP what we knew
       * rather than replace it with a stranger — on first load that is nothing either way, and on a
       * later re-read it is the difference between a blip and a logout.
       */
      .catch((caught: unknown) => {
        console.warn('[social] could not read who is signed in; keeping what we knew:', caught);
      });
    return () => { live = false; };
  }, [socialClient, manifest]);

  const selectedNode = selected ? bundle.nodes.find(n => n.id === selected) : undefined;
  /*  travels with each text so the panel can say what THAT text calls a figure —
     the tables are how "Musa" finds Moses, and reading them back is how the panel says so. */
  const texts = bundle.corpora.map(c => ({ id: c.id, name: c.name, colour: c.colour, spellingScopes: c.spellingScopes }));
  const verses = useNodeVerses(content, verseTables, selectedNode ? { id: selectedNode.id, corpora: selectedNode.corpora } : null);

  /* Hadith that name this figure. The bundle carries only a count, so nothing is fetched for a
     figure that has none — and nothing at all until a panel is opened. */
  const reportCount = selectedNode ? bundle.reportCounts?.[selectedNode.id] : undefined;
  const reports = useNodeReports(selectedNode?.id ?? null, reportCount, content);
  /* The expanded reader: the passage around the opened verse, widened on demand. */
  const context = useContextVerses(content, verseTables, reading, readBefore, readAfter);
  const openReader = (id: number): void => { setReadBefore(READ_STEP); setReadAfter(READ_STEP); setOpenPassageRef(null); setReading(id); };

  /*
   * WHERE PEOPLE DISAGREE — two lists, one surface.
   *
   * Disputed figures come from the gazetteer; contested passages come from measuring what debate
   * channels actually cited. They are the same disagreement at two granularities, so they share
   * a panel rather than each earning their own door.
   *
   * `onMap` is checked against the node set rather than assumed, because a disputed figure is not
   * necessarily drawn — offering "see it on the map" for something that is not there is the kind
   * of dead control this panel is meant to avoid.
   */
  const disputedItems = useMemo(() => {
    const onMap = new Set(bundle.nodes.map(node => node.id));
    /*
     * `category` is deliberately not forwarded, and this note exists so that is a decision
     * rather than an oversight — five fields were found unforwarded in one night, and the sixth
     * deserves an answer instead of a sixth fix.
     *
     * It carries the figure's kind: people 35, places 9, peoples 4, divine 14. Unlike the other
     * five, nothing is MISLED by its absence — a reader is not told something untrue, only
     * something less. The kind is already drawn on the map as a shape with a legend explaining
     * it, and this panel is a list of sixty-two rows where a per-row kind label would be the
     * kind of chrome the project's design deliberately avoids.
     *
     * Available if wanted: the i18n keys `type.person`, `type.place`, `type.people` and
     * `type.divine` already exist and already carry all three languages, so showing it is a
     * two-line change rather than a translation round.
     */
    return (bundle.contested ?? []).map(item => ({
      id: item.id,
      name: item.name,
      agreement: item.agreement ?? 'contested',
      note: item.note,
      onMap: onMap.has(item.id),
    }));
  }, [bundle]);

  /* Already ranked by the build (most channels first); the panel shows them in that order. */
  const contestedPassages = useMemo(
    () => (bundle.contestedPassages ?? [])
      /* Verses OR a figure. A hadith has no verse ids — it is cited as collection-and-number
         and reached through the panel of the figure it is about, so `node` is how it opens. The
         build ships neither only when nothing can open it, and drops those before they get here. */
      .filter(passage => passage.ids.length > 0 || passage.node !== undefined)
      .map(passage => ({
        ref: passage.ref,
        traditions: passage.traditions,
        channels: passage.channels,
        firstId: passage.ids[0],
        node: passage.node,
        /* Already resolved and ranked by the build; the panel shows them in that order. */
        alongside: passage.alongside ?? [],
      })),
    [bundle],
  );

  /* Open a contested passage: the reader lands on its first verse, and openReader clears any prior
     passage ref first, so the chapter citation set here is what the address bar carries. */
  /*
   * A hadith opens the FIGURE it is about, not the reader: it has no verse to land on, and its
   * text lives in that figure's report panel. Everything else lands on its first verse as before.
   */
  const openPassage = (firstId: number | undefined, ref: string, node?: string): void => {
    if (node !== undefined) { navigate(node); setOpenPassageRef(ref); return; }
    /* Neither a verse nor a figure cannot happen — the build drops those — but a bundle is data,
       and doing nothing is better than opening whatever verse id `undefined` lands on. */
    if (firstId === undefined) return;
    openReader(firstId);
    setOpenPassageRef(ref);
  };

  /* Shared passages: the episodes this figure has in both the Bible and the Quran, and the one open.
     Guarded, because an older build (or a minimal test bundle) may carry no `stories` at all. */
  const nodeStories = useMemo(
    () => (selectedNode ? (bundle.stories ?? []).filter(s => s.figure === selectedNode.id) : []),
    [bundle, selectedNode],
  );

  /* Where THIS figure's account differs between the Bible and the Quran — homed in the figure's own
     drawer beside its shared passages, so the difference is discovered by exploring the figure, not a
     menu. Matched by name, which is what a divergence carries (unlike a story, which carries the id). */
  const nodeDivergences = useMemo(
    () => (selectedNode ? (bundle.divergences ?? []).filter(d => d.figure === selectedNode.name) : []),
    [bundle, selectedNode],
  );

  /* The passage-level companion of the verse now open in the reader: the OTHER telling of a chapter told
     twice, or the OTHER verse of a differing-number pair. Surfaced as a slim ribbon in the reader, only
     when the opened verse actually has one — the "told twice"/"numbers" browses, re-homed onto the text
     they are about instead of a menu. */
  const companion = useMemo((): { label: string; onOpen: () => void } | null => {
    if (reading == null) return null;
    for (const p of bundle.internalParallels ?? []) {
      if (p.aIds.includes(reading)) return { label: t('reader.alsoTold', { ref: p.b }), onOpen: () => openTelling(p) };
      if (p.bIds.includes(reading)) return { label: t('reader.alsoTold', { ref: p.a }), onOpen: () => openTelling(p) };
    }
    for (const n of bundle.internal ?? []) {
      if (n.a?.id === reading && n.b?.ref) return { label: t('reader.alsoCounted', { ref: n.b.ref }), onOpen: () => openNumber(n) };
      if (n.b?.id === reading && n.a?.ref) return { label: t('reader.alsoCounted', { ref: n.a.ref }), onOpen: () => openNumber(n) };
    }
    return null;
  }, [reading, bundle, t]); // eslint-disable-line react-hooks/exhaustive-deps -- openTelling/openNumber only setOpenSide

  const sideRef = useMemo(
    () => (openSide ? { left: { ids: openSide.leftIds }, right: { ids: openSide.rightIds } } : null),
    [openSide],
  );
  const sideState = useSidePassages(content, verseTables, sideRef);

  /* Open a figure's shared episode (Bible ↔ Quran) side by side. */
  const openStory = (id: string): void => {
    const story = (bundle.stories ?? []).find(s => s.id === id);
    if (!story) return;
    setOpenSide({
      title: t('story.sharedTitle', { figure: story.figureName }),
      leftLabel: story.bible.range,
      rightLabel: story.quran.range,
      leftIds: story.bible.ids,
      rightIds: story.quran.ids,
      caption: story.why.length ? t('story.sharedCaption', { shared: story.why.join(', ') }) : undefined,
      link: compareValue({ kind: 'story', story: story.id }),
    });
  };

  /* Open a "told twice" retelling (two chapters inside one scripture) side by side. */
  const openTelling = (parallel: NonNullable<typeof bundle.internalParallels>[number]): void => {
    setOpenSide({
      title: t('story.toldTitle', { group: parallel.group }),
      leftLabel: parallel.a,
      rightLabel: parallel.b,
      leftIds: parallel.aIds,
      rightIds: parallel.bIds,
      caption: t('story.toldCaption', { count: parallel.shared }),
      link: compareValue({ kind: 'told', a: parallel.a, b: parallel.b }),
    });
  };

  /* Open a divergence — where the Bible and the Quran tell one episode differently — to read both. */
  const openDivergence = (divergence: NonNullable<typeof bundle.divergences>[number]): void => {
    setOpenSide({
      title: divergence.question,
      leftLabel: divergence.bible.range,
      rightLabel: divergence.quran.range,
      leftIds: divergence.bible.ids,
      rightIds: divergence.quran.ids,
      caption: divergence.caution,
      link: compareValue({ kind: 'diff', story: divergence.story }),
    });
  };

  /* Open a "numbers that differ" case — one scripture giving two figures for the same thing. */
  const openNumber = (diff: NonNullable<typeof bundle.internal>[number]): void => {
    if (diff.a?.id == null || diff.b?.id == null) return;
    setOpenSide({
      title: diff.question ?? t('compare.numbers'),
      leftLabel: diff.a.ref,
      rightLabel: diff.b.ref,
      leftIds: [diff.a.id],
      rightIds: [diff.b.id],
      caption: diff.caution,
      link: compareValue({ kind: 'numbers', a: diff.a.ref, b: diff.b.ref, counted: diff.counted }),
    });
  };

  /* Reopen a comparison from its URL id — the inverse of the `link` each open* wrote. A pairing the
     build no longer carries is skipped, so a stale link degrades to the map, never a dead end. */
  /* Returns whether the value was honoured, so an arrival can tell a reader it was not — the
     unparseable case is the one that shipped on all 36 episode pages and said nothing. */
  const restoreCompare = (value: string): boolean => {
    const link = parseCompareValue(value);
    if (!link) return false;
    switch (link.kind) {
      case 'story': {
        if (!(bundle.stories ?? []).some(s => s.id === link.story)) return false;
        openStory(link.story);
        return true;
      }
      case 'told': {
        const parallel = (bundle.internalParallels ?? []).find(p => p.a === link.a && p.b === link.b);
        if (!parallel) return false;
        openTelling(parallel);
        return true;
      }
      case 'diff': {
        const divergence = (bundle.divergences ?? []).find(d => d.story === link.story);
        if (!divergence) return false;
        openDivergence(divergence);
        return true;
      }
      case 'numbers': {
        const diff = (bundle.internal ?? []).find(n => n.a?.ref === link.a && n.b?.ref === link.b && n.counted === link.counted);
        if (!diff) return false;
        openNumber(diff);
        return true;
      }
    }
  };

  /* A discussion scope reads back as a title: a verse scope shows its citation, a figure its name. */
  const discussTitle = (key: string): string =>
    key.startsWith('verse:') ? key.slice('verse:'.length) : (bundle.nodes.find(n => n.id === key)?.name ?? key);

  /*
   * ARRIVING BY LINK — once, on mount. The URL is read and each view it names is opened, addressed by
   * the stable ids the openers wrote. Anything the current build no longer carries is skipped rather
   * than errored, so a link shared before a rebuild lands on the map, not a dead end. The `restored`
   * guard makes this run exactly once, before the sync effect below takes over reflecting state.
   */
  /* Set only by arriving at `/?signin=failed` — see `AppUrlState.signin`. */
  const [signInFailed, setSignInFailed] = useState(false);
  /* Set when an arriving URL named something this build does not hold — see `openView`. */
  const [staleLink, setStaleLink] = useState(false);

  /*
   * OPENING A VIEW, wherever it came from — an address a reader arrived with, or a leader taking
   * the room somewhere (§11).
   *
   * It was the body of the restore effect below, and §11 asks for exactly this: *"the code that
   * applies an arriving permalink is the code that applies a leader's move"*. A second copy would
   * be a second answer to what each part means, and they would differ in one of them.
   *
   * Every part is checked against what this build actually has — a node id that is not in the
   * bundle, a citation that no longer resolves — and a part that does not resolve is skipped
   * rather than erroring, so a link shared before a rebuild lands on the map and not a dead end.
   */
  /*
   * AND IT SAYS WHAT IT COULD NOT OPEN, which for a long time it did not.
   *
   * Skipping rather than erroring is right — a link shared before a rebuild should land on the map.
   * What was wrong is that it landed there in SILENCE: measured 2026-08-22, `?node=nobody`,
   * `?passage=Ezekiel 99:9`, `?compare=story:gone` and a bare compare id all left the reader on the
   * map with nothing on screen but the wordmark, their request still in the address bar, and not a
   * word about it.
   *
   * That is the failure mode that hid a real defect for the whole life of a feature. All 36 episode
   * pages emitted `?compare=<bare id>` where the app wants `story:` in front of it; every one of them
   * opened the front door and said nothing, so nobody could tell a broken link from a reader who had
   * changed their mind. A returned list of what went unmet is what makes that visible — to the
   * reader in one dismissible line, and to anyone testing it in an assertion.
   */
  const openView = useCallback((view: AppUrlState): (keyof AppUrlState)[] => {
    const unmet: (keyof AppUrlState)[] = [];
    if (view.node) {
      if (bundle.nodes.some(n => n.id === view.node)) navigate(view.node);
      else unmet.push('node');
    }
    if (view.passage) {
      const contested = contestedPassages.find(p => p.ref === view.passage);
      if (contested) openPassage(contested.firstId, contested.ref, contested.node);
      else {
        const id = refToId.get(view.passage);
        if (id !== undefined) openReader(id);
        else unmet.push('passage');
      }
    }
    if (view.compare && !restoreCompare(view.compare)) unmet.push('compare');
    if (view.panel === 'sources') setSourcesOpen(true);
    else if (view.panel === 'halls') setHallsOpen(true);
    else if (view.panel === 'disputed' && (disputedItems.length > 0 || contestedPassages.length > 0)) setDisputedOpen(true);
    else if (view.panel !== undefined) unmet.push('panel');
    if (view.discuss) setDiscussScope({ key: view.discuss, title: discussTitle(view.discuss) });
    if (view.hall) setHallSlug(view.hall);
    return unmet;
  }, [bundle.nodes, contestedPassages, disputedItems.length, navigate, openPassage, openReader, refToId, restoreCompare]);

  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const url = readAppUrl(browserUrlHost()?.search ?? '');
    /* Told only on ARRIVAL. A leader moving a room through the same opener is not somebody holding a
       stale link, and a notice about their move would be addressed to the wrong person. */
    if (openView(url).length > 0) setStaleLink(true);
    /* The one arrival that is not a view: the server sent them back from a sign-in that did not
       finish, and without this the reader is simply on the map with no idea why. */
    if (url.signin === 'failed') setSignInFailed(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- arrival only, reads the URL once

  /*
   * THE URL FOLLOWS THE STATE. Every change reflects the whole open view into the address bar with
   * replaceState (never push — see nav/permalink.ts). The reader's passage is its chapter citation
   * when a contested passage is open, else the citation of the verse it centres on.
   *
   * The FIRST run is skipped: on mount it would fire before the restore effect's opens have landed and
   * blank a valid link back out of the URL for one render. Restore already read the URL, and it writes
   * the same one it read, so there is nothing to reflect until state actually changes after mount.
   */
  /*
   * WHAT THIS READER IS LOOKING AT, in one place — the address bar reflects it, and a room shares
   * it (§11). Two descriptions of one view would be two answers to what a passage is called.
   */
  const currentView: AppUrlState = useMemo(() => ({
    node: selected ?? undefined,
    passage: reading === null ? undefined : (openPassageRef ?? bundle.refs[reading]),
    compare: openSide?.link,
    panel: sourcesOpen ? 'sources' : disputedOpen ? 'disputed' : hallsOpen ? 'halls' : undefined,
    discuss: discussScope?.key,
    hall: hallSlug ?? undefined,
  }), [selected, reading, openPassageRef, openSide, sourcesOpen, disputedOpen, hallsOpen, discussScope, hallSlug, bundle.refs]);

  /*
   * AND WHAT THE TAB SAYS, from the same state — see `nav/title.ts` for why it is worth anything.
   *
   * The passage leads where one is open: a reader who expanded a verse inside a figure is looking at
   * the verse, and it is the more precise of the two. A Hall names itself and takes the whole
   * surface, so it is left alone here.
   */
  const viewSubject = useMemo(() => {
    if (hallSlug !== null) return hallNamed?.slug === hallSlug ? hallNamed.name : null;
    if (openSide) return openSide.title;
    if (currentView.passage) return currentView.passage;
    if (selectedNode) return selectedNode.name;
    if (sourcesOpen) return t('sources.open');
    if (disputedOpen) return t('contested.open');
    return null;
  }, [hallSlug, hallNamed, openSide, currentView.passage, selectedNode, sourcesOpen, disputedOpen, t]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = documentTitle(viewSubject);
  }, [viewSubject]);

  const firstSync = useRef(true);
  useEffect(() => {
    const host = browserUrlHost();
    if (!host) return;
    if (firstSync.current) { firstSync.current = false; return; }
    syncAppUrl(host, currentView);
  }, [currentView]);

  return (
    <>
      {/* A leader's page takes the whole surface: it is what a link from their YouTube description
          lands on, and the map behind it is not what that reader came for. */}
      {hallSlug !== null && hasIdentityPlane(manifest) && (
        <HallPage
          client={socialClient}
          slug={hallSlug}
          onNamed={onHallNamed}
          meId={meId}
          /* The social plane holds node ids only (D10); the name lives on this side of the seam. */
          nodeName={nodeId => bundle.nodes.find(n => n.id === nodeId)?.name ?? nodeId}
          /* Walking into a Hall's room means going to the node it is about, where the room
             controls already are — the page closes, because the map behind it is now the point. */
          onGoToNode={nodeId => { setHallSlug(null); navigate(nodeId); }}
          onClose={() => setHallSlug(null)}
        />
      )}
      {hallsOpen && hasIdentityPlane(manifest) && (
        <HallsPanel
          client={socialClient}
          signedIn={meId !== null}
          /* Opening one closes the directory: the page IS the answer to "which hall". */
          onOpen={slug => { setHallsOpen(false); setHallSlug(slug); }}
          onClose={() => setHallsOpen(false)}
        />
      )}
      {/*
        * THE PORTED RENDERER, BEHIND `?v8=1`, IN THE SAME SOCKET.
        *
        * Same props, same callbacks, same page: the search box moves its camera, a tap opens the
        * panel, the trail lights it, and the arrival flies it. That is what makes the flag a
        * comparison rather than a demo — everything around the canvas stays exactly as it is, and
        * only what draws the map changes.
        */}
      {v8Flag ? (
        <Suspense fallback={null}><V8Map
          /* The survey shows the whole field and writes every name it can; the map does neither.
             Nothing inside the renderer can tell the two apart — see `LabelState.surveying`. */
          surveying={!arrival.open}
          nodes={nodes}
          edges={edges}
          corpusColour={corpusColour}
          traditionsOf={traditionsOf}
          words={v8Words}
          hudWords={v8HudWords}
          /* The build's own wheel, so both renderers seat the texts the same way rather than one
             using it and the other re-deriving an approximation of it. */
          wheel={bundle.wheel}
          onOrientation={setOrientation}
          onHint={setHint}
          onHover={setHovered}
          onHoverAt={setHoverAt}
          onSelect={navigate}
          focusId={selected}
          fly={arrival.stop ?? wholeField}
          fog={fog}
          selectedId={selected}
          /*
           * ABSENT WHERE THERE IS NO PLANE, not empty — and the difference is a layer.
           *
           * `usePresence` returns `new Map()` when it has nothing to poll, never `undefined`, and
           * V8Map read `presence ?? mockedPresence`, so the `??` could never fire: the seats and the
           * reader chips drew nothing under `?mock=1`, both of them ported and tested.
           *
           * THIS GATE DID NOT FIX THAT, and the reason is worth keeping rather than quietly
           * dropping. `hasIdentityPlane` reads `manifest?.social?.enabled !== false`, so a manifest
           * that says nothing about a social plane is taken to HAVE one — which the preview does,
           * so the prop stayed an empty map and the fallback stayed unreachable. Fail-open is right
           * for that function and wrong to lean on. The fallback in `V8Map` asks the question it
           * actually means instead.
           *
           * It stays because it is still true on its own terms: where a deploy says there is no
           * social plane, this prop should be absent rather than an empty map meaning the same
           * thing less clearly.
           */
          presence={hasIdentityPlane(manifest) ? presence : undefined}
          tieRefs={refs}
          off={off}
        /></Suspense>
      ) : (
        <MapCanvas
          nodes={nodes}
          edges={edges}
          washes={washes}
          sectors={sectors}
          onOrientation={setOrientation}
          onSelect={navigate}
          /* WHAT IS UNDER THE POINTER. `MapHoverCard` is built, styled and tested, and was fed by
             the ported renderer alone — so on the map every reader actually gets, it rendered for
             nobody. The canvas reports an id; the sentence is built here, by the same call the
             search row uses, so the card and the row cannot come to disagree. */
          onHover={id => {
            const node = id === null ? undefined : bundle.nodes.find(n => n.id === id);
            setHovered(node === undefined ? null : { name: node.name, sub: describeResult(node).sub });
          }}
          onHoverAt={setHoverAt}
          focusId={selected}
          fly={arrival.stop ?? wholeField}
          fog={fog}
          selectedId={selected}
        />
      )}
      {/*
        * NONE OF THE MAP'S OWN CHROME UNTIL THE ARRIVAL IS OVER.
        *
        * A search box, a legend and a rung pill behind the ground walk would hand a reader the
        * map's whole vocabulary in the same breath as telling them they do not need it yet — and
        * the walk exists precisely so that every one of those controls means something by the time
        * they see it.
        */}
      {/*
        * THE SEARCH, TOP CENTRE AND ALONE, as the reference has it — and rendered BEFORE the
        * orientation pill on purpose. Both are fixed, so the order changes no layout; what it
        * changes is that `.map-search ~ .map-orientation` can reach the pill and move it clear.
        * The pill is the passive half of the pair and so it is the half that gives way.
        */}
      {v8Flag && arrival.open && (
        <div className="map-search" data-map-chrome>
          <SearchBox
            nodes={bundle.nodes}
            onSelect={navigate}
            describe={describeResult}
            /* A typed citation opens the reader where a `?passage=` link does — the same lookup
               and the same call, so the two ways in cannot diverge. */
            holdsCitation={ref => refToId.has(ref)}
            onOpenPassage={ref => { const id = refToId.get(ref); if (id !== undefined) openReader(id); }}
          />
        </div>
      )}
      {/* The pill is the door to the travel list, as it is on theirs — only when the ported renderer
          is drawing, since the old one has no place at the foot of the map to put it. */}
      {arrival.open && (
        <MapOrientation
          at={orientation}
          corpora={bundle.corpora}
          onOpen={v8Flag ? () => { setTravelOpen(true); } : undefined}
        />
      )}
      {travelOpen && (
        <MapTravel
          regions={travelRegions}
          onGo={(id) => {
            setTravelOpen(false);
            /*
             * BOTH OPEN THE NAME, and the row says so.
             *
             * Theirs offers "look from here" for a region the reader has not walked: the camera turns
             * toward it and nothing opens. That is its frontier, and whether this map should refuse
             * to open what it has already drawn stood open with Ariel for weeks.
             *
             * DECIDED 2026-08-22, and the answer is no: *"drop the feature that prevents you from
             * going to a node"*. So "set out" is not a placeholder for a refusal that never arrived,
             * it is the label for what this map does, and a reader may open anything the map draws.
             *
             * THE FRONTIER ITSELF STAYS AND IS STILL WORTH HAVING. `fogWithin` is derived on every
             * render and the fog draws from it, so a reader can SEE how far they have been. What was
             * dropped is the gate, not the knowledge.
             */
            navigate(id);
          }}
          onClose={() => { setTravelOpen(false); }}
        />
      )}
      {/* `data-map-chrome` declares "this covers the map" where the element is written, so the
          label placer keeps names out from under it — see chromeBands in MapCanvas. */}
      {arrival.open && (
      <div className="map-overlay" data-map-chrome>
        {/*
          * THE WORDMARK IS THE DOOR, which is what theirs does with everything that is not the search.
          *
          * Their whole top bar is one row of pills — wordmark, search, who is exploring, you — and
          * every other thing a reader can reach is behind one of them. Ours put nine controls down
          * the left edge of the map, permanently, over the thing they are controls for. It is the
          * last structural difference between the two chromes and it is the one that shows in every
          * screenshot.
          *
          * The fold already existed; it was gated to phones. All that changes is that a wide screen
          * gets it too, and that the control is the wordmark instead of a second button beneath it —
          * so the corner is one pill, as theirs is, and nothing has been taken away: Halls, sources,
          * the language and the offline copy are all one press from where they were.
          *
          * NOT their menu's contents, which is a different question. Theirs holds the standpoint and
          * YOUR ATLAS and nothing else, because their prototype has no pages, no languages and no
          * offline copy. Ours holds what ours has.
          */}
        {/*
          * A SCRIM BEHIND THE OPEN MENU, which theirs has and ours did not.
          *
          * Theirs draws `<div onClick={closeMenu} style="inset:0;background:rgba(5,6,14,.45)">` under
          * its menu and the menu above it (universe-v8.dc.html:173). Two things follow from that and
          * ours had neither: the map dims, so the menu reads as the thing in front; and a press
          * anywhere else closes it, which is the gesture everybody expects of a menu.
          *
          * It also settles a collision. With the menu open the corner grows to most of the screen's
          * height, and on a phone the search — on its own row since the top bar cannot hold both —
          * crosses it. `sizes-v8.mjs` reported that at every phone width. Behind a scrim it is not a
          * collision but a thing behind a thing, which is what the scrim is for.
          *
          * `aria-hidden`, because it is not a control a keyboard should land on: Escape closes the
          * menu and the wordmark itself is the toggle.
          */}
        {moreOpen && (
          <div
            className="overlay-scrim"
            aria-hidden="true"
            onClick={() => setMoreOpen(false)}
          />
        )}
        <h1 className="overlay-mark">
          <button
            type="button"
            className="overlay-mark-toggle"
            aria-expanded={moreOpen}
            aria-controls="map-menu"
            onClick={() => setMoreOpen(open => !open)}
          >
            Verses Verse
          </button>
        </h1>
        {/*
          * THE SEARCH SITS HERE FOR THE OLD RENDERER AND IN ITS OWN BAR FOR THE PORTED ONE.
          *
          * The reference gives it the top centre, wide and alone, and it is the one control a
          * reader uses on every visit — ours is the fourth item in a rail of nine. Rendered in two
          * places rather than moved, because the old controller's chrome is a settled thing and
          * this port's job is not to redesign it on the way past. Both go when it does.
          */}
        {/* THE SEARCH EVERY ORDINARY READER GETS. The ported renderer has its own above, and a
           citation offer wired to only one of them is wired to nobody: no link on any generated
           page carries `?v8=1`, so that one is the half no reader reaches. */}
        {!v8Flag && (
          <SearchBox
            nodes={bundle.nodes}
            onSelect={navigate}
            holdsCitation={ref => refToId.has(ref)}
            onOpenPassage={ref => { const id = refToId.get(ref); if (id !== undefined) openReader(id); }}
            /* WHAT EACH RESULT IS, here too. `describe` was wired to the ported renderer's search
               alone, so the reason it exists — a row reading only a name makes a reader choose
               between things they cannot tell apart — reached nobody arriving from a page, and no
               link on any page carries `?v8=1`. It needs no v8 state: `hoverCard` and `v8Words` are
               pure, and App imports both on this path already.

               It carries the OTHER NAME a figure goes by as well — "Joseph / Yusuf" — for the same
               reason and to the same readers: a row that says what a name is, and what else it is
               called, is not a v8 feature but the library's. */
            describe={describeResult}
          />
        )}
        {/*
          A SIGN-IN THAT DID NOT FINISH, said where the sign-in controls are.

          Dismissible by pressing it, because the reader has read it by then and a notice that
          outlives its moment is furniture. It also goes on its own the first time they open
          anything, since the address bar stops carrying the parameter that produced it.
        */}
        {/* On the plane, like everything else that speaks of accounts. `?signin=failed` sets this
            straight from the URL, so without it a deployment with no sign-in at all could still
            tell a reader that their sign-in did not finish. */}
        {signInFailed && hasIdentityPlane(manifest) && (
          <button type="button" className="signin-failed" onClick={() => setSignInFailed(false)}>
            {t('auth.signInDidNotFinish')}
          </button>
        )}
        {/* The same idiom, for the same reason: a reader left on the map with no idea why. Pressing
            it is the dismissal — there is nothing else to do about it, and the map is already here. */}
        {staleLink && (
          <button type="button" className="signin-failed" onClick={() => setStaleLink(false)}>
            {t('shell.staleLink')}
          </button>
        )}
        {/* Renders nothing for a signed-out reader, which is most of them and every first visit. */}
        <Account
          client={socialClient}
          name={meName}
          onSignedOut={() => { setMeId(null); setMeName(null); setMeRole(null); }}
          /* Every row that shows a name reads it from here, so a rename reaches them all at once. */
          onRenamed={setMeName}
        />
        {/* Nothing at all unless this reader is a site moderator AND something is waiting — the
            queue that made reporting mean something, and furniture for nobody else. */}
        <ReportedPosts
          client={socialClient}
          role={meRole}
          nodeName={id => bundle.nodes.find(node => node.id === id)?.name ?? id}
          onGoToNode={navigate}
        />
        <div id="map-menu" className={moreOpen ? 'overlay-secondary overlay-secondary--open' : 'overlay-secondary'}>
        {/* How much library this build carries, and — in the tooltip — which build. It was under the
            title, on screen at all times; it belongs with the rest of what the menu answers. */}
        <p className="build" title={`build ${manifest.version}`}>
          {t('map.summary', { names: bundle.nodes.length, texts: bundle.corpora.length })}
        </p>
        <MapLegend corpora={texts} />
        {/* The reader's own state used to sit here, folded, behind the wordmark. It is in the
            corner now — see `MapYou`, and theirs, which puts it beside the count of who else is
            here. What is left in this menu is what only we have. */}
        <button type="button" className="overlay-link" onClick={() => setSourcesOpen(true)}>{t('sources.open')}</button>
        {/* A DOOR ONTO AN EMPTY ROOM IS WORSE THAN NO DOOR — the rule the line below already states
            for the disputed link, applied to the three things that need an identity plane. Without
            one, Halls, the discussion drawer and a Hall page fetch routes that do not exist. Each is
            gated where it is OFFERED and again where it RENDERS, because a URL can reach the second
            without passing the first.

            IN BRACES, because in JSX children a bare block comment is not a comment: React renders
            it, so this paragraph was on screen in the corner of the map, in every language. It was
            not only ugly — the overlay shrink-wraps its content, so five lines of prose made that
            column as wide as the viewport, and in Hebrew the panel then covered the account row and
            swallowed presses on Sign out. Found by the right-to-left geometry gate. */}
        {hasIdentityPlane(manifest) && (
          <button type="button" className="overlay-link" onClick={() => setHallsOpen(true)}>{t('halls.browse')}</button>
        )}
        {/* Shown only when the build carries something to show — a door onto an empty room is
            worse than no door. Older bundles carry neither list and the link stays away. */}
        {(disputedItems.length > 0 || contestedPassages.length > 0) && (
          <button type="button" className="overlay-link" onClick={() => setDisputedOpen(true)}>{t('contested.open')}</button>
        )}
        <LanguageSwitcher />
        {offlineSupported && (
          <OfflineButton download={onProgress => requestOfflineDownload(navigator.serviceWorker.controller, offlineFiles, onProgress)} />
        )}
        </div>
      </div>
      )}
      {/*
        * THE ARRIVAL — the flight, and the one name it leaves the reader standing on. Both draw
        * OVER the canvas rather than instead of it: the field is the thing being shown, and the
        * words are only ever in front of it.
        */}
      <Tour
        stop={arrival.stop}
        names={bundle.nodes.length}
        passages={bundle.meta.total}
        texts={bundle.corpora.length}
        /* Counted, not typed: how many traditions actually hold the texts this build carries. */
        traditions={new Set(bundle.corpora.flatMap(c => c.traditions ?? [])).size}
        onSkip={() => { arrival.skip(); navigate(null); }}
      />
      {/*
        * THE LANDING SCREEN STOOD HERE and is gone — Ariel, 2026-08-22: "drop the feature that
        * prevents you from going to a node ... it requires reading linked nodes".
        *
        * `Ground` was the arrival's last frame: the name it landed on, one passage, and a way onward
        * that was a name tapped INSIDE that passage. Its chips were deliberately the fallback rather
        * than the path, so a reader who wanted to move had to read first. That is the requirement
        * that was dropped, and the screen went with it — it had no other job.
        *
        * The flight is unchanged: it still comes down onto a figure and the dark still closes. The
        * effect above navigates to `landedId`, so the reader now arrives with that name's panel
        * open on the map, free to close it and tap any other.
        */}
      {/*
        * THE FILTER ROW, along the bottom, and only with the renderer that honours it.
        *
        * `map/v8/web.ts` reads `c_<corpus>` and `k_<kind>`; the old controller has no filter at
        * all, so showing the row beside it would be nine controls that do nothing. It goes when
        * that controller does.
        */}
      {/*
        * THE READER'S OWN CORNER, IN THE ROW WITH THE COUNT OF EVERYBODY ELSE — which is where theirs
        * is, and it had a corner of its own until it was found sitting on top of this cluster's feed.
        * One corner claimed by two rules finds itself; the cluster lends it a row instead.
        *
        * Only with the ported renderer, like the rest of this chrome: the old one has no corner to
        * put it in and goes when it does.
        */}
      {v8Flag && arrival.open && (
        <MapPresenceCluster
          exploring={social.exploring}

          rooms={social.rooms}
          invented={mocked}
          beside={
            <MapYou
              open={youOpen}
              onToggle={() => setYouOpen(open => !open)}
              onClose={() => setYouOpen(false)}
              walked={trail.trail.visited.length}
            >
              <MapStandpoint
                trail={trail.trail}
                standpoint={trail.standpoint}
                record={trailRecord}
                nameOf={id => nodes.find(n => n.id === id)?.label ?? null}
                atlas={atlas}
                rungName={id => {
                  const tier = nodes.find(n => n.id === id)?.tier;
                  return tier === undefined ? null : t(`tier.${tier}.name` as keyof Strings);
                }}
              />
            </MapYou>
          }
        />
      )}
      {v8Flag && arrival.open && <MapFilterBar chips={filterChips} off={off} onToggle={toggleFilter} />}
      {/*
        * SOMEBODY ELSE READING — over the arrival as well as over the map, which is the point.
        *
        * Theirs has a remark on screen three seconds after a reader arrives; ours flew them over the
        * field and set them down alone. Not gated on `arrival.open`, unlike everything above it:
        * the first second is exactly where it earns its place.
        */}

      {arrival.open && hovered !== null && selected === null && (
        <MapHoverCard name={hovered.name} sub={hovered.sub} x={hoverAt.x} y={hoverAt.y} />
      )}
      {arrival.open && !hintDismissed && <MapHint onDismiss={() => setHintDismissed(true)} hint={hint} />}
      {arrival.open && selectedNode && (
        <NodePanel
          key={selectedNode.id} /* fresh filter state per figure */
          node={selectedNode}
          /* The bundle flags who else answers to this name but stores only ids; the panel does
             no lookups, so they are resolved here where every node is to hand. */
          sharesWith={(selectedNode.shares ?? [])
            .map(id => bundle.nodes.find(n => n.id === id))
            .filter((n): n is NonNullable<typeof n> => !!n)
            .map(n => ({ id: n.id, name: n.name }))}
          /* Claims that this figure's name and others name one being. Resolved here for the same
             reason `sharesWith` is: the bundle stores ids, and every node is to hand here. The
             figure itself is dropped from `others` — a panel does not link to the panel it is. */
          identities={(selectedNode.identities ?? [])
            .map(id => (bundle.identities ?? []).find(g => g.id === id))
            .filter((g): g is NonNullable<typeof g> => !!g)
            .map(g => ({
              id: g.id,
              claim: g.claim,
              dispute: g.dispute,
              others: g.names
                .filter(other => other !== selectedNode.id)
                .map(other => bundle.nodes.find(n => n.id === other))
                .filter((n): n is NonNullable<typeof n> => !!n)
                .map(n => ({ id: n.id, name: n.name })),
            }))}
          /* The merged case needs no resolving — it is this node's own. */
          dispute={selectedNode.dispute}
          /* Arguments that turn on passages. The bundle carries them once and names the nodes
             each is reached from, so the filter happens here where every node is to hand. */
          passageDisputes={(bundle.passageDisputes ?? [])
            .filter(d => d.nodes.includes(selectedNode.id))
            .map(d => ({ id: d.id, dispute: d.dispute, passages: d.passages }))}
          texts={texts}
          verses={verses}
          onJump={navigate}
          onExpand={openReader}
          stories={nodeStories.map(s => ({ id: s.id, bibleRange: s.bible.range, quranRange: s.quran.range }))}
          onOpenStory={openStory}
          divergences={nodeDivergences.map(d => ({ question: d.question, bibleRange: d.bible.range, quranRange: d.quran.range }))}
          onOpenDivergence={i => { const d = nodeDivergences[i]; if (d) openDivergence(d); }}
          onOpenDiscussion={hasIdentityPlane(manifest) ? () => setDiscussScope({ key: selectedNode.id, title: selectedNode.name }) : undefined}
          onDiscussVerse={(ref) => setDiscussScope({ key: `verse:${ref}`, title: ref })}
          canGoBack={canGoBack}
          onBack={back}
          /*
           * ONLY WHERE THERE IS A PLANE TO TALK TO — 2026-08-18.
           *
           * `Rooms` fetches this figure's sessions and rooms on mount. On a content-only deploy those
           * routes do not exist, so every figure a reader opened made two doomed requests AND drew a
           * "Talk about this" control that could never work.
           *
           * The same rule as `auth/me` above, and finding it here is why the rule belongs in one place:
           * that fix stopped at the one call it was looking at rather than at the class. Seen in a real
           * browser — 404s for nodes/<id>/sessions and nodes/<id>/rooms on every figure opened.
           */
          social={hasIdentityPlane(manifest) ? <Rooms
            /* Shared reading (§11): the room shares what this reader is looking at, and opens
               what a leader points at with the same function an arriving link uses. */
            view={currentView}
            onFollowed={openView}
            client={socialClient}
            connector={livekitConnector}
            nodeId={selectedNode.id}
            meId={meId}
            nodeName={selectedNode.name}
            /* The same name table the Hall page reads, so a leader's own study somewhere else is
               named rather than shown as an id — and clicking it goes there. */
            nodeNameOf={nodeId => bundle.nodes.find(n => n.id === nodeId)?.name ?? nodeId}
            onGoToNode={nodeId => navigate(nodeId)}
          /> : undefined}
          onOpenRef={(ref) => { const id = refToId.get(ref); if (id !== undefined) openReader(id); }}
          /* Only offered where there is a map to narrow: the old renderer has no filter row. */
          onOnlyCorpus={v8Flag ? onlyCorpus : undefined}
          trails={trails}
          /*
           * KEYED ON THE STATUS, NOT ON THE ROW COUNT — see `ReportsList`. Keying on length made a
           * failed fetch indistinguishable from a figure with no hadith, which is the one thing
           * `useNodeReports` was written to keep apart.
           */
          reports={reports.reports.length > 0 || reports.status === 'failed'
            ? {
              name: selectedNode.name,
              reports: reports.reports,
              total: reportCount ?? reports.reports.length,
              failed: reports.status === 'failed',
              /* Counted over the whole list by the build, because a page of 200 that leads with the
                 ones NAMING the figure reads 0 narrated for every busy narrator. */
              narrated: reports.narrated,
              /* A figure's reports arrive a page at a time since 2026-08-20 — see
                 `useNodeReports`, and the 19.2 MB file that made one fetch untenable. */
              hasMore: reports.hasMore,
              loadMore: reports.loadMore,
            }
            : undefined}
          onClose={() => { navigate(null); setDiscussScope(null); }}
        />
      )}
      {disputedOpen && (
        <DisputedPanel
          items={disputedItems}
          passages={contestedPassages}
          onOpen={id => { setDisputedOpen(false); navigate(id); }}
          /* A passage has one thing worth doing — read it. Opening it closes the list, because
             leaving a panel stacked over what it just opened is a click the reader has to spend
             getting back to the text they asked for. */
          onOpenPassage={(firstId, ref, node) => { setDisputedOpen(false); openPassage(firstId, ref, node); }}
          onDebate={(id, name) => { setDisputedOpen(false); setDiscussScope({ key: id, title: name }); }}
          onClose={() => setDisputedOpen(false)}
        />
      )}
      {reading != null && (
        <ReadingDrawer
          unit={context.unit}
          centerId={context.centerId}
          verses={context}
          canLoadBefore={context.status === 'ready' && readBefore < READ_MAX}
          canLoadAfter={context.status === 'ready' && readAfter < READ_MAX}
          onLoadMore={direction => {
            if (direction === 'before') setReadBefore(b => Math.min(READ_MAX, b + READ_STEP));
            else setReadAfter(a => Math.min(READ_MAX, a + READ_STEP));
          }}
          companion={companion}
          /* A name in the passage still travels the map — jumping closes the reader and selects it. */
          onJump={(id) => { setReading(null); navigate(id); }}
          onExpand={openReader}
          onClose={() => setReading(null)}
        />
      )}
      {openSide && (
        <SideBySide
          title={openSide.title}
          leftLabel={openSide.leftLabel}
          rightLabel={openSide.rightLabel}
          left={{ status: sideState.status, passages: sideState.left, incomplete: sideState.incomplete }}
          right={{ status: sideState.status, passages: sideState.right, incomplete: sideState.incomplete }}
          caption={openSide.caption}
          onJump={(id) => { setOpenSide(null); navigate(id); }}
          onClose={() => setOpenSide(null)}
        />
      )}
      {sourcesOpen && (
        <SourcesPanel
          /* The four scriptures, then the hadith — one list, because a reader asking "whose
             words are these" is asking one question. The reports are not a corpus (no verses,
             no packed code letter), so they arrive on their own field and are mapped into the
             same shape rather than given a second list of their own.

             ONE ENTRY PER SUPPLIER since 2026-08-17. Each carries its own licence, digitisation
             claim and `relation` sentence, so they cannot be merged into a single row without
             printing one supplier's terms over another's text. */
          texts={[
            ...bundle.corpora.map(sourceTextFor),
            ...(bundle.reportSources ?? []).map(sourceTextForReports),
          ]}
          heldBack={(bundle.heldBack ?? []).map(h => heldBackFor(h, id => bundle.corpora.find(c => c.id === id)?.longName))}
          absent={ABSENT_BRANCHES}
          /* Held, licensed, and waiting on us — the third kind of missing, and the only one
             where a reader is owed an explanation about this project rather than about a
             publisher or about the world. */
          unshelved={UNSHELVED}
          /* And the fourth kind: missing only until a date. From the bundle rather than a schema
             constant, because the build drops rows whose day has already come — a passed date is
             an alarm for the pipeline, not a promise to a reader. */
          expiries={bundle.expiries ?? []}
          onClose={() => setSourcesOpen(false)}
        />
      )}
      {/*
        * GATED ON THE PLANE, like the Halls beside it and the Rooms above it.
        *
        * It was not, and `?discuss=` opens purely from the URL — so on a deployment whose manifest
        * says `social: {"enabled": false}`, which is what production says today, arriving on a
        * discussion link opened a drawer backed by a client that cannot answer it. Nothing links
        * there yet because the static pages gate the invitation too, but a link built while the
        * flag was on, or a page a crawler kept from that window, would have found it.
        */}
      {discussScope && hasIdentityPlane(manifest) && (
        <DiscussionDrawer
          meId={meId}
          client={socialClient}
          nodeId={discussScope.key}
          title={discussScope.title}
          onClose={() => setDiscussScope(null)}
        />
      )}
    </>
  );
}

function CenteredStatus(
  { children, role, error, live, actions }: {
    children: React.ReactNode;
    role: 'status' | 'alert';
    error?: boolean;
    live?: 'polite' | 'assertive';
    /** What a reader can do about it. Absent while loading, where there is nothing to do but wait. */
    actions?: React.ReactNode;
  },
): React.JSX.Element {
  return (
    <main className="shell">
      <p className={error ? 'status status-error' : 'status'} role={role} aria-live={live}>{children}</p>
      {/* OUTSIDE the live region: a screen reader announces the sentence, and the controls are then
          found by the reader moving through them, rather than read out as part of the message. */}
      {actions && <p className="status-actions">{actions}</p>}
    </main>
  );
}

/** A reader is told what went wrong in the words core already chose; anything else is `String`. */
function describeError(error: unknown): string {
  return error instanceof ApiError ? error.message : String(error);
}
